/* global URL_MENU */
import { useState, useCallback, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import type { ConnectionHookResult, ProviderCode } from '@interfaces/Integration';
import { MetaCloudConnectionState, MetaCloudConnectionResponse } from '../types';
import { useSyncIntegration, useRemoveIntegration, useGetIntegrations } from '../../../../hooks';
import { PROVIDER_TYPE_MAP, Integration } from '@utils/constants';
import { useCommerceFromStore } from '@store/actions/commerce';
import { loadFacebookSdk, launchEmbeddedSignup } from '../constants';
import { getServerErrorMessage } from '../errors';

const INITIAL_STATE: MetaCloudConnectionState = {
  connectionStatus: 'DISCONNECTED',
  isConnecting: false,
  phoneNumber: null,
  instanceName: null,
  error: null,
};

interface UseMetaCloudConnectionProps {
  id?: string;
  provider_code: ProviderCode;
  provider_type: keyof typeof PROVIDER_TYPE_MAP;
  configuration: Record<string, unknown>;
}

export const useMetaCloudConnection = (integration?: UseMetaCloudConnectionProps): ConnectionHookResult => {
  const { syncIntegration } = useSyncIntegration();
  const { removeIntegration } = useRemoveIntegration();
  const { refreshIntegrations } = useGetIntegrations();
  const commerce = useCommerceFromStore();

  const lastCheckedIdRef = useRef<string | null>(null);
  const isDisconnectingRef = useRef(false);

  const [isCheckingStatus, setIsCheckingStatus] = useState(() => Boolean(integration?.id));

  const [state, setState] = useState<MetaCloudConnectionState>(() => {
    if (integration?.id) {
      return {
        ...INITIAL_STATE,
        connectionStatus: 'CONNECTED',
        instanceName: (integration.configuration?.instance_name as string) || null,
        phoneNumber: (integration.configuration?.phone_number as string) || null,
      };
    }
    return { ...INITIAL_STATE };
  });

  const handleConnectionResponse = useCallback(
    (response: MetaCloudConnectionResponse, showToast = false) => {
      if (response.status === 'CONNECTED') {
        setState((prev) => {
          if (isDisconnectingRef.current) return prev;
          if (prev.connectionStatus === 'CONNECTED') return prev;

          return {
            ...prev,
            isConnecting: false,
            connectionStatus: 'CONNECTED',
            instanceName: response.instanceName || null,
            phoneNumber: response.phoneNumber || null,
            error: null,
          };
        });

        if (!isDisconnectingRef.current) {
          refreshIntegrations();
          if (showToast) {
            toast.success('¡WhatsApp Cloud API conectado!');
          }
        }
      } else if (response.status === 'DISCONNECTED' || response.status === 'ERROR') {
        setState((prev) => ({
          ...prev,
          isConnecting: false,
          // Preservar 'ERROR' del backend para que MetaConnectErrorNote muestre el error;
          // mapearlo a 'DISCONNECTED' lo ocultaría (solo se vería en excepciones de red).
          connectionStatus: response.status === 'ERROR' ? 'ERROR' : 'DISCONNECTED',
          error: response.message || 'Desconectado',
        }));
      }
    },
    [refreshIntegrations],
  );

  const fetchStatus = useCallback(async () => {
    try {
      const response = (await syncIntegration(integration as unknown as Integration, {
        action: 'SHOW_STATUS',
        notify: false,
      })) as unknown as MetaCloudConnectionResponse;

      if (response) {
        handleConnectionResponse(response);
        return response;
      }
    } catch {
      return null;
    } finally {
      setIsCheckingStatus(false);
    }
  }, [integration, syncIntegration, handleConnectionResponse]);

  // Check real status on mount — once per ID
  useEffect(() => {
    if (integration?.id && integration.id !== lastCheckedIdRef.current) {
      lastCheckedIdRef.current = integration.id;
      fetchStatus();
    }
  }, [integration?.id, fetchStatus]);

  // Preload Facebook SDK when component mounts
  useEffect(() => {
    loadFacebookSdk().catch(() => {
      // SDK load failure is non-fatal; error will surface when user clicks Connect
    });
  }, []);

  /**
   * Opens Meta's Embedded Signup popup via FB.login().
   * The code comes back via the JS callback — no redirect needed.
   */
  const connect = useCallback(async () => {
    // Resetear el estado de error en el reintento: si quedó en 'ERROR', limpiarlo a
    // 'DISCONNECTED' para no mostrar el botón en loading y el mensaje de error a la vez.
    setState((prev) => ({
      ...prev,
      isConnecting: true,
      error: null,
      connectionStatus: prev.connectionStatus === 'ERROR' ? 'DISCONNECTED' : prev.connectionStatus,
    }));

    try {
      await loadFacebookSdk();
      // Precarga la pantalla "Completa la información de tu empresa" del diálogo de Meta con
      // los datos del comercio en sesión. El comercio no tiene campo de sitio web propio: su
      // URL pública es el menú (`URL_MENU/id_path`), que es lo que Meta debe mostrar.
      const result = await launchEmbeddedSignup({
        name: commerce?.nombre,
        email: commerce?.correo_contacto,
        website: commerce?.id_path ? `${URL_MENU}/${commerce.id_path}` : '',
        country: commerce?.address?.country,
      });

      if (!result) {
        // User cancelled the popup
        setState((prev) => ({ ...prev, isConnecting: false }));
        return;
      }

      // Exchange code for token via backend. En Coexistence el popup solo devuelve
      // `waba_id`; el backend resuelve el `phone_number_id` desde la WABA si no llega.
      const response = (await syncIntegration(
        (integration || {
          provider_code: 'metaCloudApi',
          provider_type: 'communication',
        }) as unknown as Integration,
        {
          action: 'CONNECT',
          notify: false,
          config: {
            code: result.code,
            waba_id: result.wabaId,
            phone_number_id: result.phoneNumberId,
            featureType: result.featureType,
          },
        },
      )) as unknown as MetaCloudConnectionResponse;

      if (response) {
        handleConnectionResponse(response, true);
      }
    } catch (err) {
      // El backend manda el diagnóstico real en el body del 4xx ("No se encontraron
      // numeros de telefono...", "No se encontro una cuenta de WhatsApp Business
      // compartida..."). Sustituirlo siempre por el genérico dejaba al comercio sin
      // saber qué arreglar en su cuenta de Meta; el genérico queda solo de fallback.
      setState((prev) => ({
        ...prev,
        isConnecting: false,
        connectionStatus: 'ERROR',
        error: getServerErrorMessage(err, 'Error al conectar con Meta. Intenta nuevamente.'),
      }));
    }
  }, [integration, syncIntegration, handleConnectionResponse, commerce]);

  const disconnect = useCallback(async () => {
    isDisconnectingRef.current = true;

    if (!integration?.id) {
      lastCheckedIdRef.current = null;
      setState(INITIAL_STATE);
      isDisconnectingRef.current = false;
      return;
    }

    setState((prev) => ({ ...prev, isConnecting: true }));

    try {
      await removeIntegration(integration as unknown as Integration);
      lastCheckedIdRef.current = null;
      setState(INITIAL_STATE);
    } catch {
      setState((prev) => ({ ...prev, isConnecting: false }));
    } finally {
      isDisconnectingRef.current = false;
    }
  }, [integration, removeIntegration]);

  return {
    isConnected: state.connectionStatus === 'CONNECTED',
    isConnecting: state.isConnecting,
    isCheckingStatus,
    connect,
    disconnect,
    status: state.connectionStatus,
    instanceName: state.instanceName,
    // Se expone para que la card pueda pintar el motivo real del fallo. Sin esto el
    // mensaje del servidor moría dentro del hook.
    error: state.error,
    // OAuth popup flow: no QR needed
    isQrVisible: false,
    qrCode: null,
    onVerify: undefined,
    onCloseQr: undefined,
    isVerifying: false,
  };
};
